/**
 * (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  Button,
  Icon,
  ListItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  OrderedList,
  Text,
  VStack,
} from "@microbit/ui";
import { RiInformationLine } from "react-icons/ri";
import { FormattedMessage } from "react-intl";

interface NativeBluetoothPairingLostDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onTryAgain: () => void;
}

/**
 * Error dialog for iOS Bluetooth pairing information has been lost.
 * This can happen when the user flashes the paired micro:bit using the computer.
 */
const NativeBluetoothPairingLostDialog = ({
  isOpen,
  onClose,
  onTryAgain,
}: NativeBluetoothPairingLostDialogProps) => {
  return (
    <Modal
      isDismissable={false}
      motionless
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "xl" }}
      isCentered
    >
      <ModalHeader>
        <FormattedMessage id="native-bluetooth-error-heading" />
      </ModalHeader>
      <ModalBody>
        <VStack width="100%" alignItems="left" gap={5}>
          <Text textAlign="left" w="100%">
            <FormattedMessage id="native-bluetooth-error-forget-device" />
          </Text>
          <VStack textAlign="left" w="100%" gap={3}>
            <OrderedList textAlign="left">
              <ListItem>
                <FormattedMessage id="native-bluetooth-error-forget-device-step-1" />
              </ListItem>
              <ListItem>
                <FormattedMessage
                  id="native-bluetooth-error-forget-device-step-2"
                  values={{
                    infoIcon: (chunks: React.ReactNode) => (
                      <Icon
                        as={RiInformationLine}
                        css={{
                          width: "1.2em",
                          height: "1.2em",
                          verticalAlign: "text-bottom",
                          // Depicts the ⓘ button in iOS Settings' Bluetooth
                          // list, so iOS system blue - not a brand ramp.
                          color: "#007AFF",
                        }}
                        // The translated chunk content is a string at runtime.
                        // eslint-disable-next-line @typescript-eslint/no-base-to-string
                        aria-label={String(chunks)}
                      />
                    ),
                  }}
                />
              </ListItem>
              <ListItem>
                <FormattedMessage id="native-bluetooth-error-forget-device-step-3" />
              </ListItem>
              <ListItem>
                <FormattedMessage id="native-bluetooth-error-forget-device-step-4" />
              </ListItem>
            </OrderedList>
          </VStack>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button onPress={onClose} variant="secondary" size="lg">
          <FormattedMessage id="cancel-action" />
        </Button>
        <Button onPress={onTryAgain} variant="primary" size="lg">
          <FormattedMessage id="try-again-action" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default NativeBluetoothPairingLostDialog;
